import { useEffect, useRef, useState } from 'react';
import { useGoogleIdentity } from '@/hooks/use-google-identity';
import { supabase } from '@/lib/supabase';
import { handlePostAuthTasks } from '@/lib/auth-utils';

interface GoogleSignInButtonProps {
  onSuccess: (user: any) => void;
  onError?: (error: any) => void;
  className?: string;
  buttonConfig?: {
    type?: 'standard' | 'icon';
    theme?: 'outline' | 'filled_blue' | 'filled_black';
    size?: 'large' | 'medium' | 'small';
    text?: 'signin_with' | 'signup_with' | 'continue_with';
    shape?: 'rectangular' | 'pill' | 'circle' | 'square';
    logo_alignment?: 'left' | 'center';
    width?: number;
    locale?: string;
  };
}

// Helper function to ensure user ID is uppercase
const transformUser = (user: any): any => {
  if (!user) return null;
  return {
    ...user,
    id: user.id.toUpperCase()
  }
}

export function GoogleSignInButton({
  onSuccess,
  onError,
  className = '',
  buttonConfig = {}
}: GoogleSignInButtonProps) {
  const { isLoaded, renderSignInButton } = useGoogleIdentity();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const buttonId = useRef(`google-signin-${Math.random().toString(36).substr(2, 9)}`);

  const handleGoogleSignIn = async (credential: string) => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      console.log('Processing Google Sign In credential...');

      // Use Supabase's signInWithIdToken to authenticate with the Google ID token
      const { data, error } = await supabase.auth.signInWithIdToken({
        provider: 'google',
        token: credential,
      });

      if (error) {
        throw new Error('Error authenticating with Google credential: ' + error.message);
      }

      console.log('Google Sign In successful:', data);

      // Handle post-auth tasks (profile creation, tracking, etc.)
      if (data.user) {
        // Run post-auth tasks in the background
        handlePostAuthTasks(data.user, data.session, 'google_button').catch(error => {
          console.warn('Post-auth tasks failed:', error);
        });
      }

      // Return the user immediately - no need to wait for auth context
      const transformedUser = data.user ? transformUser(data.user) : null;
      onSuccess(transformedUser);
    } catch (error) {
      console.error('Error in Google Sign In:', error);
      onError?.(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGoogleError = (error: any) => {
    console.error('Google Sign In error:', error);
    setIsProcessing(false);
    onError?.(error);
  };

  useEffect(() => {
    if (isLoaded && buttonRef.current) {
      const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      if (!googleClientId) {
        console.error('Google Client ID not configured');
        return;
      }

      renderSignInButton(
        buttonId.current,
        googleClientId,
        handleGoogleSignIn,
        handleGoogleError,
        {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'center',
          ...buttonConfig,
        }
      );
    }
  }, [isLoaded]);

  if (!isLoaded) {
    return (
      <div className={`flex items-center justify-center p-3 border border-gray-300 rounded-lg ${className}`}>
        <div className="animate-pulse text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div
      ref={buttonRef}
      id={buttonId.current}
      className={`google-signin-button ${className} ${isProcessing ? 'pointer-events-none opacity-50' : ''}`}
    />
  );
}
